import React, { useCallback, useLayoutEffect, useMemo } from 'react';
import { View } from 'react-native';
import * as RNLocalize from 'react-native-localize';
import Animated, { useAnimatedScrollHandler } from 'react-native-reanimated';
import { useAppSelector } from 'store/hooks';
import { AppView, Button, ButtonGroup, CurrencyAmountText, rem, SCROLL_INDICATOR_INSETS, Spacer } from 'design-system';
import { HEIGHT } from 'design-system/Button';
import EmojiText from 'design-system/EmojiText';
import withAnimatedNavigationHeader from 'design-system/hoc/withAnimatedNavigationHeader';
import NavigationCloseButton from 'design-system/NavigationCloseButton';
import Squircle from 'design-system/Squircle';
import StatusBar from 'design-system/StatusBar';
import StatusIcon from 'design-system/StatusIcon';
import Text from 'design-system/Text';
import useAppFrameDimensions from 'hooks/useAppFrameDimensions';
import useColors from 'hooks/useColors';
import { useMarginBottom } from 'hooks/useMarginBottom';
import useStyles from 'hooks/useStyles';
import ProgressCard from 'features/invest/components/InvestOnboardingFunnel/ProgressCard';
import { useConnectedAccount } from 'features/invest/hooks/selectors';
import { formatDateInProgressTimeline } from 'features/invest/utils';
import { selectPotProductFromProductId, selectShouldShowPotsList } from 'features/pots/reducers/selectors';
import { getPotDisplayName } from 'features/pots/utils';
import { selectAutosaveStatusObj } from 'reducers/selectors';
import createStyleSheets from 'utils/createStyleSheets';
import { goBackToTopWithStops } from 'utils/navigationv6';
import { isWeb } from '../../../constants';
const isUKTimezone = RNLocalize.getTimeZone() === 'Europe/London';
const DepositTimeline = withAnimatedNavigationHeader(({ scrollY, navigation, route }) => {
    const colors = useColors();
    const styles = useStyles(styleSet);
    const connectedAccount = useConnectedAccount();
    const autosaveStatusObj = useAppSelector(selectAutosaveStatusObj);
    const shouldShowPotsList = useAppSelector(selectShouldShowPotsList);
    const savingsPlanMandateStatus = useAppSelector((state) => state.pots.savingsPlan?.mandateStatus);
    const { depositEta, amount, emoji, potName, timestamp, depositDetails, isBankTransfer, potId, potProductId } = route.params;
    const potProduct = useAppSelector((state) => selectPotProductFromProductId(state, potProductId));
    const handleClose = useCallback(() => {
        if (!shouldShowPotsList) {
            goBackToTopWithStops(navigation, ['Report']);
            return;
        }
        navigation.pop();
    }, [shouldShowPotsList]);
    const handleContinue = useCallback(() => {
        navigation.pop();
        if (!savingsPlanMandateStatus) {
            navigation.navigate('AutosaveIntro');
            return;
        }
        if (autosaveStatusObj?.actionRequired) {
            navigation.navigate('AutoSave');
            return;
        }
        navigation.navigate('AutosaveDistribution', { isEditFlow: true, createdPotId: potId });
    }, [autosaveStatusObj?.actionRequired, potId, savingsPlanMandateStatus]);
    useLayoutEffect(() => {
        navigation.setOptions({
            headerLeft: () => <NavigationCloseButton onPress={handleClose} inWebHeader/>,
            headerBackVisible: false,
        });
    }, [handleClose]);
    const scrollHandler = useAnimatedScrollHandler((handle) => {
        // eslint-disable-next-line no-param-reassign
        scrollY.value = handle.contentOffset.y;
    });
    const { paddingHorizontalStyle } = useAppFrameDimensions(isWeb);
    const contentContainerStyle = useMarginBottom('paddingBottom', HEIGHT + rem(16), undefined, paddingHorizontalStyle);
    const Content = useMemo(() => {
        const formattedInitiatedTime = formatDateInProgressTimeline(timestamp);
        const dateTimeString = depositEta ? formatDateInProgressTimeline(depositEta, true) : undefined;
        const estimatedTimeInfo = dateTimeString
            ? `Estimated by ${dateTimeString}${isUKTimezone ? '' : ' UK time'}`
            : undefined;
        const arrivesInDesc = potProduct?.noticeDays
            ? `Once your funds arrive, they’ll start earning interest. You can withdraw with ${potProduct.noticeDays} days’ notice.`
            : 'Once your funds arrive, they’ll start earning interest. You can withdraw at any time.';
        if (depositDetails?.method === 'singleDirectDebit') {
            return (<>
            <ProgressCard lightBg completed noTextDecoration alwaysShowTimeline alwaysShowChildren title="Direct Debit initiated" timeInfo={formattedInitiatedTime}>
              <Text Secondary TextThin-14>
                We’ve sent the Direct Debit request to your bank.
              </Text>
            </ProgressCard>

            <ProgressCard title={`Collecting from ${connectedAccount?.bankName || 'Linked account'}`} lightBg isActiveStep noTextDecoration completed={false}>
              <Text Secondary TextThin-14>
                We’re collecting the payment. This usually takes 5 working days.
              </Text>
            </ProgressCard>

            <ProgressCard lightBg lastItem completed={false} noTextDecoration timeInfo={estimatedTimeInfo} title={`Arrives in ${getPotDisplayName(potName)}`}>
              <Text TextThin-14 Secondary>
                {arrivesInDesc}
              </Text>
            </ProgressCard>
          </>);
        }
        return (<>
          <ProgressCard lightBg completed noTextDecoration alwaysShowTimeline title="Deposit requested" timeInfo={formattedInitiatedTime}/>

          <ProgressCard title={isBankTransfer
                ? `Moving from ${connectedAccount?.bankName || 'Linked account'}`
                : 'Moving from Emma wallet'} lightBg isActiveStep noTextDecoration completed={false}>
            <Text Secondary TextThin-14>
              Your funds should arrive in 1 working day.
            </Text>
          </ProgressCard>

          <ProgressCard lightBg lastItem completed={false} noTextDecoration timeInfo={estimatedTimeInfo} title={`Arrives in ${getPotDisplayName(potName)}`}>
            <Text TextThin-14 Secondary>
              {arrivesInDesc}
            </Text>
          </ProgressCard>
        </>);
    }, [
        connectedAccount?.bankName,
        depositDetails?.method,
        depositEta,
        isBankTransfer,
        potName,
        potProduct?.noticeDays,
        timestamp,
    ]);
    return (<AppView withFrame={false}>
        <StatusBar barStyle="dark-content" animated/>
        <Animated.ScrollView scrollEventThrottle={16} onScroll={scrollHandler} keyboardDismissMode="on-drag" contentContainerStyle={contentContainerStyle} scrollIndicatorInsets={SCROLL_INDICATOR_INSETS}>
          <Spacer h={24}/>
          <View style={styles.amountInfo}>
            <View>
              <View style={styles.emojiContainer}>
                <View style={styles.emojiContainerInner}>
                  <EmojiText fontSize={rem(26)}>{emoji}</EmojiText>
                </View>
              </View>
              <View style={styles.statusIconContainer}>
                <Squircle width={rem(24)} height={rem(24)} color={colors.cards.onDark}>
                  <StatusIcon state={'in-progress'} height={20} width={20}/>
                </Squircle>
              </View>
            </View>
            <Spacer h={24}/>
            <Text Text-16 Secondary>
              Deposit request
            </Text>
            <Spacer h={8}/>
            <CurrencyAmountText size="XL" value={{ amount, currency: 'GBP' }}/>
          </View>
          <Spacer h={8}/>
          {Content}
          <Spacer h={24}/>
        </Animated.ScrollView>
        <ButtonGroup type="floating">
          {potId ? (<Button title="Continue" brand onPress={handleContinue}/>) : (<Button title="Close" brand onPress={handleClose}/>)}
        </ButtonGroup>
      </AppView>);
}, ({ colors }) => ({
    title: ``,
    headerColorOutputRange: [colors.background.dark, colors.cards.onDark],
}));
const styleSet = createStyleSheets((colors) => ({
    amountInfo: {
        alignItems: 'center',
    },
    emojiContainer: {
        width: rem(64),
        height: rem(64),
        borderRadius: rem(64),
        alignItems: 'center',
        justifyContent: 'center',
        backgroundColor: colors.cards.onDark,
    },
    emojiContainerInner: {
        width: rem(52),
        height: rem(52),
        borderRadius: rem(52),
        alignItems: 'center',
        justifyContent: 'center',
        backgroundColor: colors.background.dark,
    },
    statusIconContainer: {
        right: 0,
        bottom: 0,
        position: 'absolute',
    },
}));
export default DepositTimeline;
